// JSON-LD SoftwareApplication structured data for /tool/:slug pages.
//
// Why this exists: Google's rich results for software/apps surface the
// tool's name, category, price, and (if available) aggregate star rating
// directly in the SERP. Without structured data we just get a plain blue
// link. We inject the script tag imperatively (no react-helmet dep) and
// clean up on unmount so navigating between tools doesn't leave stale
// schema in the DOM.
//
// CRITICAL: Google penalises fabricated aggregateRating / review data, so
// we ONLY emit `aggregateRating` when there is at least one real review in
// the database. Same with `offers` — we map our pricing buckets to real
// price values where possible and otherwise omit the field rather than
// guess.
import { useEffect } from "react";
import { useReviews } from "@/hooks/useReviews";

type Props = {
  slug: string;
  name: string;
  description: string;
  category: string;
  subcategory?: string;
  pricing: string;
  url: string; // canonical destination (the tool's own site)
};

// Map our internal pricing buckets to schema.org `offers.price` values.
// Only "Free" + "Open Source" have a deterministic price (0). For
// Freemium / Paid we omit price (Google accepts offers without a price as
// long as priceCurrency or priceSpecification is absent too).
function buildOffers(pricing: string) {
  const lower = pricing.toLowerCase();
  if (lower === "free" || lower === "open source") {
    return {
      "@type": "Offer",
      price: "0",
      priceCurrency: "USD",
      availability: "https://schema.org/InStock",
    };
  }
  // Freemium → describe as a free tier with paid upgrades.
  if (lower === "freemium") {
    return {
      "@type": "Offer",
      price: "0",
      priceCurrency: "USD",
      availability: "https://schema.org/InStock",
      description: "Free tier with paid upgrades available",
    };
  }
  // Paid / unknown → no offer node (Google prefers omission to bad data).
  return null;
}

const SCRIPT_ID = "tool-jsonld";

const ToolJsonLd = ({
  slug,
  name,
  description,
  category,
  subcategory,
  pricing,
  url,
}: Props) => {
  const { reviews, averageRating } = useReviews(slug);

  useEffect(() => {
    if (!slug || !name) return;

    const offers = buildOffers(pricing);

    const data: Record<string, unknown> = {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      name,
      description,
      applicationCategory: category,
      // Most listed tools are web apps; mark as such so Google can render
      // the right rich-result template.
      operatingSystem: "Web",
      url,
      // The HackerLab listing page is the "main entity of page" for SEO.
      mainEntityOfPage: `https://hackerlabs.ai/tool/${slug}`,
    };

    if (subcategory) {
      data.applicationSubCategory = subcategory;
    }

    if (offers) {
      data.offers = offers;
    }

    // Only emit aggregateRating with REAL reviews — never fabricated.
    if (reviews.length > 0 && averageRating > 0) {
      data.aggregateRating = {
        "@type": "AggregateRating",
        ratingValue: Number(averageRating.toFixed(1)),
        reviewCount: reviews.length,
        bestRating: 5,
        worstRating: 1,
      };
    }

    // Replace any previous script (handles client-side nav between tools).
    const existing = document.getElementById(SCRIPT_ID);
    if (existing) existing.remove();

    const script = document.createElement("script");
    script.id = SCRIPT_ID;
    script.type = "application/ld+json";
    script.text = JSON.stringify(data);
    document.head.appendChild(script);

    return () => {
      const node = document.getElementById(SCRIPT_ID);
      if (node) node.remove();
    };
  }, [slug, name, description, category, subcategory, pricing, url, reviews.length, averageRating]);

  return null;
};

export default ToolJsonLd;
